import { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import moment from 'moment';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { COLLA } from '../../utils/utils';
import { formatMoney } from '../../utils/money';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, ChartDataLabels);

function MerxandatgeRevenueChart({ orders = [], coverageThreshold = 90, breakdownByItems = false, title = 'Ingressos per comandes', deletedItemIds = new Set() }) {
    const [availableYears, setAvailableYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);

    useEffect(() => {
        const yearsSet = new Set();
        orders.forEach(o => {
            const date = moment(o.created_at);
            const seasonYear = COLLA === 'sarri'
                ? date.year()
                : (date.month() >= 8 ? date.year() : date.year() - 1);
            yearsSet.add(seasonYear);
        });
        const years = Array.from(yearsSet).sort((a, b) => b - a);
        setAvailableYears(years);
        if (!selectedYear && years.length > 0) {
            setSelectedYear(years[0]);
        }
    }, [orders]);

    const monthLabels = COLLA === 'sarri'
        ? ['Gen', 'Feb', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Des']
        : ['Set', 'Oct', 'Nov', 'Des', 'Gen', 'Feb', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago'];

    const colorPalette = [
        'rgba(99, 102, 241, 0.8)', // Indigo
        'rgba(34, 197, 94, 0.8)',  // Green
        'rgba(14, 165, 233, 0.8)', // Sky
        'rgba(244, 114, 182, 0.8)', // Pink
        'rgba(234, 179, 8, 0.8)',   // Amber
        'rgba(139, 92, 246, 0.8)',  // Purple
        'rgba(239, 68, 68, 0.8)',   // Red
        'rgba(20, 184, 166, 0.8)',  // Teal
        'rgba(248, 113, 113, 0.8)', // Light red
        'rgba(52, 211, 153, 0.8)'   // Emerald
    ];

    const getItemLabel = item => {
        // Return only the base item name, without variant details
        const name = item?.name || 'Sense nom';
        if (item?.itemId && deletedItemIds.has(item.itemId)) {
            return `${name} (Arxivat)`;
        }
        return name;
    };

    const { datasets, total } = useMemo(() => {
        if (!breakdownByItems) {
            // Simple mode: just sum totals per month (like main branch)
            const counts = new Array(12).fill(0);

            if (selectedYear) {
                orders.forEach(o => {
                    if (!['pending', 'processing', 'completed'].includes(o.status) || (o.status === 'pending' && o.stripe_payment_id)) return;
                    const date = moment(o.created_at);
                    const seasonYear = COLLA === 'sarri'
                        ? date.year()
                        : (date.month() >= 8 ? date.year() : date.year() - 1);
                    if (seasonYear !== selectedYear) return;
                    let index = date.month();
                    if (COLLA !== 'sarri') {
                        index = date.month() >= 8 ? date.month() - 8 : date.month() + 4;
                    }
                    counts[index] += parseFloat(o.total || 0);
                });
            }

            const total = counts.reduce((sum, val) => sum + val, 0);

            return {
                datasets: [{
                    label: 'Ingressos (€)',
                    data: counts.map(v => parseFloat(v.toFixed(2))),
                    backgroundColor: colorPalette[0],
                    datalabels: {
                        anchor: 'end',
                        align: 'top'
                    }
                }],
                total
            };
        }

        // Breakdown mode: by items with grouping
        const itemRevenueMap = new Map();

        if (selectedYear) {
            orders.forEach(o => {
                if (!['pending', 'processing', 'completed'].includes(o.status) || (o.status === 'pending' && o.stripe_payment_id)) return;
                const date = moment(o.created_at);
                const seasonYear = COLLA === 'sarri'
                    ? date.year()
                    : (date.month() >= 8 ? date.year() : date.year() - 1);
                if (seasonYear !== selectedYear) return;

                let index = date.month();
                if (COLLA !== 'sarri') {
                    index = date.month() >= 8 ? date.month() - 8 : date.month() + 4;
                }

                (o.items || []).forEach(item => {
                    const label = getItemLabel(item);
                    const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
                    const unitPrice = parseFloat(item.price) || 0;
                    const revenue = unitPrice * quantity;

                    if (!itemRevenueMap.has(label)) {
                        itemRevenueMap.set(label, new Array(12).fill(0));
                    }
                    const monthlyTotals = itemRevenueMap.get(label);
                    monthlyTotals[index] += revenue;
                });
            });
        }

        const entries = Array.from(itemRevenueMap.entries());

        if (entries.length === 0) {
            return {
                datasets: [{
                    label: 'Ingressos (€)',
                    data: new Array(12).fill(0),
                    backgroundColor: colorPalette[0],
                    datalabels: { display: false }
                }],
                total: 0
            };
        }

        // Calculate total revenue first
        const total = entries.reduce((sum, [, values]) => sum + values.reduce((a, v) => a + v, 0), 0);

        // Calculate totals for each item
        const itemTotals = entries.map(([label, values]) => ({
            label,
            values,
            total: values.reduce((a, v) => a + v, 0)
        }));

        // Sort items by revenue descending
        itemTotals.sort((a, b) => b.total - a.total);

        // Use Pareto approach: show items until cumulative coverage reaches threshold
        const majorItems = [];
        const minorItems = [];
        let cumulativeRevenue = 0;
        const threshold = total * (coverageThreshold / 100);

        itemTotals.forEach(item => {
            if (cumulativeRevenue < threshold) {
                majorItems.push(item);
                cumulativeRevenue += item.total;
            } else {
                minorItems.push(item);
            }
        });

        // Create "Altres" dataset if there are minor items
        const finalEntries = [...majorItems];
        if (minorItems.length > 0) {
            const altresValues = new Array(12).fill(0);
            minorItems.forEach(item => {
                item.values.forEach((value, monthIndex) => {
                    altresValues[monthIndex] += value;
                });
            });
            finalEntries.push({
                label: 'Altres',
                values: altresValues,
                total: altresValues.reduce((a, v) => a + v, 0)
            });
        }

        const datasets = finalEntries.map((item, index) => ({
            label: item.label,
            data: item.values.map(v => parseFloat(v.toFixed(2))),
            backgroundColor: colorPalette[index % colorPalette.length],
            datalabels: {
                anchor: 'center',
                align: 'center'
            }
        }));

        return { datasets, total };
    }, [COLLA, orders, selectedYear, coverageThreshold, breakdownByItems]);

    const data = {
        labels: monthLabels,
        datasets
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: breakdownByItems ? { position: 'bottom' } : { display: false },
            tooltip: {
                callbacks: {
                    label: (context) => `${context.dataset.label}: ${formatMoney(context.raw)}€`
                }
            },
            datalabels: {
                color: '#666',
                font: { weight: 'bold', size: 10 },
                formatter: v => v > 0 ? `${Math.round(v)}€` : '',
                display: (context) => {
                    const value = context.dataset.data[context.dataIndex];
                    if (value <= 0) return false;
                    
                    // Get chart dimensions and scale
                    const chart = context.chart;
                    const chartArea = chart.chartArea;
                    const chartHeight = chartArea.bottom - chartArea.top;
                    const yScale = chart.scales.y;
                    
                    // Calculate pixel height of this bar segment
                    const maxValue = yScale.max;
                    const pixelHeight = (value / maxValue) * chartHeight;
                    
                    // Hide label if bar height is less than 10px
                    return pixelHeight >= 10;
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grace: '10%', // Add 10% extra space at the top
                ticks: { callback: value => formatMoney(value) + '€' },
                stacked: breakdownByItems
            },
            x: {
                stacked: breakdownByItems
            }
        }
    };

    if (availableYears.length === 0) return null;

    return (
        <div style={{ marginBottom: 30 }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
                height: '30px'
            }}>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', height: '100%' }}>
                    {title}
                </h3>
                <div style={{ color: '#666', fontSize: '16px', display: 'flex', alignItems: 'center', height: '100%' }}>
                    Total: <strong style={{ marginLeft: 5 }}>{formatMoney(total)}€</strong>
                </div>
            </div>
            <select
                value={selectedYear || ''}
                onChange={e => setSelectedYear(parseInt(e.target.value))}
                style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: '#f8f8f8',
                    fontSize: '14px',
                    color: '#444',
                    cursor: 'pointer',
                    outline: 'none',
                    marginBottom: 15
                }}
            >
                {availableYears.map(year => (
                    <option key={year} value={year}>
                        {COLLA === 'sarri' ? `${year}` : `Setembre ${year} - Agost ${year + 1}`}
                    </option>
                ))}
            </select>
            <div style={{ width: '100%', height: window.innerWidth > 768 ? 300 : 280 }}>
                <Bar data={data} options={options} />
            </div>
        </div>
    );
}

export default MerxandatgeRevenueChart;
